import { validateFutCardPersonalizationStep } from "@theme/product-form";
import { preloadImage } from "@theme/utilities";

const PERSONALIZATION_OPEN_CLASS = "fut-card-personalization-open";
const POPUP_SELECTOR = "[data-fut-card-personalization-popup]";
const TRIGGER_SELECTOR = "[data-fut-card-personalize-trigger]";
const CLOSE_SELECTOR = "[data-fut-card-personalization-close]";
const STEP_SELECTOR = "[data-fut-card-personalization-step]";
const STEP_BLOCK_SELECTOR = "[data-fut-card-personalization-step-block]";
const STEP_INDICATOR_SELECTOR =
  "[data-fut-card-personalization-step-indicator]";
const PROGRESS_STEPS_SELECTOR =
  "[data-fut-card-personalization-progress-steps]";
const BACK_SELECTOR = "[data-fut-card-personalization-back]";
const NEXT_SELECTOR = "[data-fut-card-personalization-next]";
const PROGRESS_SUMMARY_SELECTOR =
  "[data-fut-card-personalization-progress-summary]";
const PROGRESS_FILL_SELECTOR = "[data-fut-card-personalization-progress-fill]";
const REVIEW_CHECKOUT_SELECTOR = "[data-fut-card-review-checkout]";
const REVIEW_CHECKOUT_MOUNT_SELECTOR = "[data-fut-card-review-checkout-mount]";
const PREVIEW_SELECTOR = "[data-fut-card-personalization-preview]";
const STEPS_CONTAINER_SELECTOR = "[data-fut-card-personalization-steps]";
const MOBILE_MEDIA_QUERY = "(max-width: 749px)";
const IMAGE_OPTION_IMAGE_SELECTOR = ".fut-card-image-options__option-image";
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const DEFAULT_STEP_PARAM = "step";
const MIN_STEP = 1;
const EDITOR_STATE_KEY = "editor-save-state-fut-card-personalization-popup";

/**
 * @returns {{ isOpen?: boolean, step?: number } | null}
 */
function readFutCardEditorSessionState() {
  const raw = sessionStorage.getItem(EDITOR_STATE_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** @type {boolean} */
let globalThemeEditorEventsBound = false;

/**
 * @param {typeof FutCardPersonalization} FutCardPersonalizationClass
 */
function ensureFutCardGlobalThemeEditorEvents(FutCardPersonalizationClass) {
  if (globalThemeEditorEventsBound) return;

  document.addEventListener("shopify:block:select", (event) => {
    if (!window.Shopify?.designMode) return;

    /** @type {{ blockId?: string } | undefined} */
    const detail =
      "detail" in event
        ? /** @type {{ blockId?: string }} */ (event.detail)
        : undefined;
    const blockId = detail?.blockId;
    const target = event.target;
    const selectedElement = target instanceof Element ? target : null;

    /** @type {InstanceType<typeof FutCardPersonalizationClass> | null} */
    let openingComponent = null;

    for (const element of document.querySelectorAll(
      "fut-card-personalization",
    )) {
      if (!(element instanceof FutCardPersonalizationClass)) continue;
      if (
        element.shouldOpenEditorPopupForBlockSelect(blockId, selectedElement)
      ) {
        openingComponent = element;
        break;
      }
    }

    if (openingComponent) {
      openingComponent.handleEditorPopupBlockSelect(
        blockId ?? "",
        selectedElement,
      );
      return;
    }

    const savedState = readFutCardEditorSessionState();

    if (savedState?.isOpen) {
      for (const element of document.querySelectorAll(
        "fut-card-personalization",
      )) {
        if (!(element instanceof FutCardPersonalizationClass)) continue;
        if (
          element.shouldOpenEditorPopupForBlockSelect(blockId, selectedElement)
        ) {
          element.handleEditorPopupBlockSelect(blockId ?? "", selectedElement);
          return;
        }
      }

      const shouldKeepPopupOpenForReload =
        !blockId ||
        Array.from(document.querySelectorAll("fut-card-personalization")).some(
          (element) => {
            if (!(element instanceof FutCardPersonalizationClass)) return false;
            return element.shouldOpenEditorPopupForBlockSelect(
              blockId,
              selectedElement,
            );
          },
        );

      if (shouldKeepPopupOpenForReload) {
        for (const element of document.querySelectorAll(
          "fut-card-personalization",
        )) {
          if (element instanceof FutCardPersonalizationClass) {
            element.restoreEditorPopupFromSavedState();
          }
        }
        return;
      }
    }

    for (const element of document.querySelectorAll(
      "fut-card-personalization",
    )) {
      if (element instanceof FutCardPersonalizationClass) {
        element.closeEditorPopupIfOpen();
      }
    }
  });

  document.addEventListener("shopify:section:select", () => {
    if (!window.Shopify?.designMode) return;

    for (const element of document.querySelectorAll(
      "fut-card-personalization",
    )) {
      if (element instanceof FutCardPersonalizationClass) {
        element.closeEditorPopupIfOpen();
      }
    }
  });

  document.addEventListener("shopify:section:load", (event) => {
    if (!window.Shopify?.designMode) return;

    const restoreOpenPopup = readFutCardEditorSessionState()?.isOpen === true;

    requestAnimationFrame(() => {
      for (const element of document.querySelectorAll(
        "fut-card-personalization",
      )) {
        if (!(element instanceof FutCardPersonalizationClass)) continue;
        element.handleEditorSectionLoad(event);
        if (restoreOpenPopup) {
          element.restoreEditorPopupFromSavedState();
        }
      }
    });
  });

  globalThemeEditorEventsBound = true;
}

/**
 * @param {HTMLImageElement} image
 * @returns {string[]}
 */
function getImagePreloadUrls(image) {
  const urls = [];
  const src = image.currentSrc || image.src;

  if (src) urls.push(src);

  return urls;
}

/**
 * @param {Set<string>} preloadedUrls
 * @param {ParentNode | null | undefined} root
 */
function preloadCardOptionImages(preloadedUrls, root) {
  if (!root) return;

  root.querySelectorAll(IMAGE_OPTION_IMAGE_SELECTOR).forEach((element) => {
    if (!(element instanceof HTMLImageElement)) return;

    for (const url of getImagePreloadUrls(element)) {
      if (!url || preloadedUrls.has(url)) continue;
      preloadedUrls.add(url);
      preloadImage(url);
    }
  });
}

/**
 * @param {string} paramName
 * @returns {boolean}
 */
function hasPersonalizationParam(paramName) {
  const url = new URL(window.location.href);
  const value = url.searchParams.get(paramName);
  return value === "1" || value === "true";
}

/**
 * @param {string} paramName
 * @param {string} [stepParamName]
 * @returns {boolean}
 */
function pushPersonalizationParam(
  paramName,
  stepParamName = DEFAULT_STEP_PARAM,
) {
  const url = new URL(window.location.href);
  url.searchParams.set(paramName, "1");
  url.searchParams.delete(stepParamName);

  const nextUrl = url.toString();
  if (nextUrl === window.location.href) return false;

  window.history.pushState(
    { futCardOpen: true, futCardStep: MIN_STEP },
    "",
    nextUrl,
  );
  return true;
}

/**
 * @param {string} paramName
 */
function replacePersonalizationParam(paramName) {
  const url = new URL(window.location.href);
  url.searchParams.set(paramName, "1");
  window.history.replaceState({}, "", url.toString());
}

/**
 * @param {string} paramName
 * @param {string} [stepParamName]
 */
function clearPersonalizationParam(
  paramName,
  stepParamName = DEFAULT_STEP_PARAM,
) {
  const url = new URL(window.location.href);
  let changed = false;

  if (url.searchParams.has(paramName)) {
    url.searchParams.delete(paramName);
    changed = true;
  }

  if (url.searchParams.has(stepParamName)) {
    url.searchParams.delete(stepParamName);
    changed = true;
  }

  if (!changed) return;
  window.history.replaceState({}, "", url.toString());
}

/**
 * @param {string} paramName
 * @param {number} totalSteps
 * @returns {number}
 */
function getStepFromUrl(paramName, totalSteps) {
  const url = new URL(window.location.href);
  const raw = url.searchParams.get(paramName);
  const step = Number.parseInt(raw ?? "", 10);

  if (Number.isNaN(step) || step < MIN_STEP) return MIN_STEP;
  if (step > totalSteps) return totalSteps;
  return step;
}

/**
 * @param {number} step
 * @param {string} paramName
 * @param {boolean} [replace]
 * @returns {boolean}
 */
function setStepParam(step, paramName, replace = true) {
  const url = new URL(window.location.href);

  if (step <= MIN_STEP) {
    url.searchParams.delete(paramName);
  } else {
    url.searchParams.set(paramName, String(step));
  }

  const nextUrl = url.toString();
  if (nextUrl === window.location.href) return false;

  if (replace) {
    window.history.replaceState({ futCardStep: step }, "", nextUrl);
  } else {
    window.history.pushState({ futCardStep: step }, "", nextUrl);
  }

  return true;
}

class FutCardPersonalization extends HTMLElement {
  /** @type {HTMLElement | null} */
  #popup = null;

  /** @type {HTMLButtonElement | null} */
  #trigger = null;

  /** @type {Comment | null} */
  #popupPlaceholder = null;

  /** @type {HTMLElement | null} */
  #reviewCheckout = null;

  /** @type {Comment | null} */
  #reviewCheckoutPlaceholder = null;

  /** @type {string} */
  #queryParam = "personalize";

  /** @type {string} */
  #stepParam = DEFAULT_STEP_PARAM;

  /** @type {number} */
  #totalSteps = 1;

  /** @type {number} */
  #currentStep = MIN_STEP;

  /** @type {HTMLElement[]} */
  #stepBlocks = [];

  /** @type {boolean} */
  #isDesignMode = false;

  /** @type {boolean} */
  #isOpen = false;

  /** @type {number} */
  #historyDepth = 0;

  /** @type {string} */
  #stepLabelTemplate = "";

  /** @type {boolean} */
  #hideUnavailableNavButtons = false;

  /** @type {Set<string>} */
  #preloadedImageUrls = new Set();

  /** @type {boolean} */
  #popupAccessibilityBound = false;

  /** @type {Map<string, number>} */
  #editorStepMap = new Map();

  /** @type {Set<string>} */
  #reviewCheckoutEditorBlockIds = new Set();

  /** @type {AbortController | undefined} */
  #lifecycleAbortController;

  /** @type {MediaQueryList | undefined} */
  #mobileMediaQuery;

  connectedCallback() {
    this.#popup = this.querySelector(POPUP_SELECTOR);
    this.#trigger = this.querySelector(TRIGGER_SELECTOR);
    this.#queryParam = this.dataset.queryParam || "personalize";
    this.#stepParam = this.#popup?.dataset.stepParam || DEFAULT_STEP_PARAM;
    this.#stepLabelTemplate = this.#popup?.dataset.stepLabelTemplate ?? "";
    this.#hideUnavailableNavButtons =
      this.#popup?.dataset.navButtonAvailability === "hidden";
    this.#isDesignMode =
      this.#popup?.dataset.designMode === "true" ||
      Boolean(window.Shopify?.designMode);
    this.#initializeSteps();

    if (this.#popup && !this.#isDesignMode) {
      this.#popupPlaceholder = document.createComment(
        "fut-card-personalization-popup",
      );
      this.#popup.replaceWith(this.#popupPlaceholder);
    }

    if (!this.#isDesignMode) {
      this.#reserveReviewCheckout();
    }

    this.#bindTriggerEvents();
    this.#bindPopupControls();

    this.#mobileMediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    this.#mobileMediaQuery.addEventListener(
      "change",
      this.#handleMobileLayoutChange,
    );

    window.addEventListener("popstate", this.#handlePopState);

    if (this.#isDesignMode || window.Shopify?.designMode) {
      this.#loadEditorStepMap();
      ensureFutCardGlobalThemeEditorEvents(FutCardPersonalization);
      queueMicrotask(() => this.#restoreEditorState());
    }

    if (!this.#isDesignMode && hasPersonalizationParam(this.#queryParam)) {
      this.open({ replaceStepHistory: true });
    }

    this.#lifecycleAbortController?.abort();
    this.#lifecycleAbortController = new AbortController();
    document.addEventListener("product-form:reset", () => this.reset(), {
      signal: this.#lifecycleAbortController.signal,
    });
  }

  disconnectedCallback() {
    if (this.#isDesignMode || window.Shopify?.designMode) {
      this.#saveEditorState();
    } else {
      this.closeEditorPopupIfOpen();
    }

    this.#unbindTriggerEvents();
    this.#unbindPopupControls();
    this.#mobileMediaQuery?.removeEventListener(
      "change",
      this.#handleMobileLayoutChange,
    );
    window.removeEventListener("popstate", this.#handlePopState);
    this.#unbindPopupAccessibility();
    this.#restorePopup();
    this.#restoreReviewCheckout();
    this.#lifecycleAbortController?.abort();
  }

  reset() {
    if (this.#isOpen && !this.#isDesignMode) {
      this.close();
    }
  }

  #bindTriggerEvents() {
    this.#trigger?.addEventListener("click", this.#handleTriggerClick);
    this.#trigger?.addEventListener(
      "pointerenter",
      this.#handleTriggerPointerEnter,
    );
  }

  #unbindTriggerEvents() {
    this.#trigger?.removeEventListener("click", this.#handleTriggerClick);
    this.#trigger?.removeEventListener(
      "pointerenter",
      this.#handleTriggerPointerEnter,
    );
  }

  #bindPopupControls() {
    this.#popup?.querySelectorAll(CLOSE_SELECTOR).forEach((button) => {
      button.addEventListener("click", this.#handleCloseClick);
    });
    this.#popup
      ?.querySelector(BACK_SELECTOR)
      ?.addEventListener("click", this.#handleBackClick);
    this.#popup
      ?.querySelector(NEXT_SELECTOR)
      ?.addEventListener("click", this.#handleNextClick);
  }

  #unbindPopupControls() {
    this.#popup?.querySelectorAll(CLOSE_SELECTOR).forEach((button) => {
      button.removeEventListener("click", this.#handleCloseClick);
    });
    this.#popup
      ?.querySelector(BACK_SELECTOR)
      ?.removeEventListener("click", this.#handleBackClick);
    this.#popup
      ?.querySelector(NEXT_SELECTOR)
      ?.removeEventListener("click", this.#handleNextClick);
  }

  /** @param {MouseEvent} event */
  #handleTriggerClick = (event) => {
    event.preventDefault();
    if (this.#isDesignMode) return;
    this.open({ resetStep: true });
  };

  #handleTriggerPointerEnter = () => {
    this.#preloadStepCardOptionImages(MIN_STEP);
  };

  /**
   * @param {number} [step]
   */
  #preloadStepCardOptionImages(step = MIN_STEP) {
    preloadCardOptionImages(
      this.#preloadedImageUrls,
      this.#stepBlocks[step - 1],
    );
  }

  /** @param {MouseEvent} event */
  #handleCloseClick = (event) => {
    event.preventDefault();
    this.close();
  };

  /** @param {MouseEvent} event */
  #handleBackClick = (event) => {
    event.preventDefault();
    if (this.#currentStep <= MIN_STEP) return;

    if (this.#isDesignMode) {
      this.goToStep(this.#currentStep - 1);
      return;
    }

    window.history.back();
  };

  /** @param {MouseEvent} event */
  #handleNextClick = (event) => {
    event.preventDefault();
    if (this.#currentStep >= this.#totalSteps) return;
    this.goToStep(this.#currentStep + 1, { pushHistory: true });
  };

  #handleMobileLayoutChange = () => {
    this.#applyResponsiveStepLayout();
  };

  /**
   * Apply the layout that matches the current viewport. On mobile every step
   * is shown on a single scrolling page; on desktop the stepped navigation is
   * restored for the active step.
   */
  #applyResponsiveStepLayout() {
    if (!this.#popup) return;

    if (this.#mobileMediaQuery?.matches) {
      this.#enableMobileSinglePage();
    } else {
      this.#applyStep(this.#currentStep);
      this.#positionPreview();
    }
  }

  /** Reveal all steps at once and keep the review checkout on its step. */
  #enableMobileSinglePage() {
    if (!this.#popup) return;

    this.#stepBlocks.forEach((stepBlock) => {
      stepBlock.hidden = false;
      stepBlock.toggleAttribute("data-active", true);
    });

    const reviewStep = this.#findReviewCheckoutStep();
    if (reviewStep != null) {
      this.#syncReviewCheckoutMount(reviewStep);
    }

    this.#positionPreview();
  }

  /**
   * Place the live preview. On mobile it flows between the last regular step
   * and the review step (i.e. right after the stats step); on desktop it sits
   * as the first column, before the steps container.
   */
  #positionPreview() {
    if (!this.#popup) return;

    const preview = this.#popup.querySelector(PREVIEW_SELECTOR);
    const stepsContainer = this.#popup.querySelector(STEPS_CONTAINER_SELECTOR);
    if (
      !(preview instanceof HTMLElement) ||
      !(stepsContainer instanceof HTMLElement)
    ) {
      return;
    }

    const body = stepsContainer.parentElement;
    if (!(body instanceof HTMLElement)) return;

    if (this.#mobileMediaQuery?.matches) {
      const reviewStep = this.#stepBlocks.find(
        (stepBlock) =>
          stepBlock.parentElement === stepsContainer &&
          stepBlock.querySelector(REVIEW_CHECKOUT_MOUNT_SELECTOR),
      );

      if (reviewStep) {
        if (preview.nextElementSibling !== reviewStep) {
          stepsContainer.insertBefore(preview, reviewStep);
        }
      } else if (preview.parentElement !== stepsContainer) {
        stepsContainer.appendChild(preview);
      }

      return;
    }

    // Desktop: restore the preview ahead of the steps column.
    if (
      preview.parentElement !== body ||
      preview.nextElementSibling !== stepsContainer
    ) {
      body.insertBefore(preview, stepsContainer);
    }
  }

  /**
   * @returns {number | null}
   */
  #findReviewCheckoutStep() {
    for (let index = 0; index < this.#stepBlocks.length; index += 1) {
      if (
        this.#stepBlocks[index]?.querySelector(REVIEW_CHECKOUT_MOUNT_SELECTOR)
      ) {
        return index + 1;
      }
    }

    return null;
  }

  #handlePopState = () => {
    if (!this.#isOpen) {
      if (hasPersonalizationParam(this.#queryParam)) {
        this.open({ replaceStepHistory: true });
      }
      return;
    }

    if (!hasPersonalizationParam(this.#queryParam)) {
      this.#historyDepth = 0;
      this.close({ skipHistory: true });
      return;
    }

    const previousStep = this.#currentStep;
    const step = getStepFromUrl(this.#stepParam, this.#totalSteps);

    if (step < previousStep) {
      this.#historyDepth = Math.max(0, this.#historyDepth - 1);
    } else if (step > previousStep) {
      this.#historyDepth++;
    }

    this.#applyStep(step, { updateHistory: false });
  };

  #initializeSteps() {
    if (!this.#popup) return;

    const stepBlocks = Array.from(
      this.#popup.querySelectorAll(STEP_BLOCK_SELECTOR),
    ).filter((element) => element instanceof HTMLElement);

    this.#stepBlocks = stepBlocks;
    this.#totalSteps = Math.max(stepBlocks.length, 1);

    stepBlocks.forEach((stepBlock, index) => {
      const stepNumber = index + 1;
      stepBlock.setAttribute(
        "data-fut-card-personalization-step",
        String(stepNumber),
      );
    });

    this.#buildProgressIndicators(stepBlocks);

    if (this.#isDesignMode || window.Shopify?.designMode) {
      this.#loadEditorStepMap();
    }
  }

  /**
   * @param {HTMLElement[]} stepBlocks
   */
  #buildProgressIndicators(stepBlocks) {
    if (!this.#popup) return;

    const progressRoot = this.#popup.querySelector(
      "[data-fut-card-personalization-progress]",
    );
    if (progressRoot instanceof HTMLElement) {
      progressRoot.style.setProperty(
        "--fut-progress-step-count",
        String(Math.max(stepBlocks.length, 1)),
      );
    }

    const progressSteps = this.#popup.querySelector(PROGRESS_STEPS_SELECTOR);
    if (!(progressSteps instanceof HTMLOListElement)) return;

    progressSteps.replaceChildren();

    stepBlocks.forEach((stepBlock, index) => {
      const stepNumber = index + 1;
      const progressLabel =
        stepBlock.dataset.progressLabel?.trim() ||
        stepBlock
          .querySelector(".fut-card-personalization-popup__step-heading")
          ?.textContent?.trim() ||
        `Step ${stepNumber}`;

      const indicator = document.createElement("li");
      indicator.className = "fut-card-personalization-popup__progress-step";
      indicator.setAttribute(
        "data-fut-card-personalization-step-indicator",
        String(stepNumber),
      );
      if (stepNumber === MIN_STEP) {
        indicator.setAttribute("data-state", "current");
      }

      const marker = document.createElement("span");
      marker.className = "fut-card-personalization-popup__progress-marker";
      marker.setAttribute("aria-hidden", "true");

      const number = document.createElement("span");
      number.className = "fut-card-personalization-popup__progress-number";
      number.textContent = String(stepNumber);
      marker.appendChild(number);

      const label = document.createElement("span");
      label.className = "fut-card-personalization-popup__progress-label";
      label.textContent = progressLabel;

      indicator.append(marker, label);
      progressSteps.appendChild(indicator);
    });
  }

  #mountPopup() {
    if (!this.#popup || this.#popup.parentElement === document.body) return;

    if (!this.#popupPlaceholder) {
      this.#popupPlaceholder = document.createComment(
        "fut-card-personalization-popup",
      );
      this.#popup.replaceWith(this.#popupPlaceholder);
    }

    document.body.appendChild(this.#popup);
  }

  #restorePopup() {
    if (!this.#popup || !this.#popupPlaceholder) return;
    if (this.#popup.parentElement !== document.body) return;

    this.#popupPlaceholder.parentNode?.insertBefore(
      this.#popup,
      this.#popupPlaceholder,
    );
    this.#popupPlaceholder.remove();
    this.#popupPlaceholder = null;
  }

  #reserveReviewCheckout() {
    if (
      this.#reviewCheckout instanceof HTMLElement &&
      this.#reviewCheckoutPlaceholder
    )
      return;

    const reviewCheckout = document.querySelector(REVIEW_CHECKOUT_SELECTOR);
    if (!(reviewCheckout instanceof HTMLElement)) return;

    this.#reviewCheckout = reviewCheckout;
    this.#reviewCheckoutPlaceholder = document.createComment(
      "fut-card-review-checkout",
    );
    reviewCheckout.replaceWith(this.#reviewCheckoutPlaceholder);
    reviewCheckout.hidden = true;
  }

  #ensureReviewCheckoutReserved() {
    if (
      this.#reviewCheckout instanceof HTMLElement &&
      this.#reviewCheckoutPlaceholder
    )
      return;
    this.#reserveReviewCheckout();
  }

  #restoreReviewCheckout() {
    const reviewCheckout = this.#reviewCheckout;
    const placeholder = this.#reviewCheckoutPlaceholder;
    if (!(reviewCheckout instanceof HTMLElement) || !placeholder) return;

    if (
      reviewCheckout.parentElement !== placeholder.parentNode &&
      placeholder.parentNode
    ) {
      placeholder.parentNode.insertBefore(reviewCheckout, placeholder);
    }

    reviewCheckout.hidden = true;
  }

  /**
   * @param {number} step
   */
  #syncReviewCheckoutMount(step) {
    this.#ensureReviewCheckoutReserved();

    const reviewCheckout = this.#reviewCheckout;
    const activeStepBlock = this.#stepBlocks[step - 1];
    const mount = activeStepBlock?.querySelector(
      REVIEW_CHECKOUT_MOUNT_SELECTOR,
    );
    const placeholder = this.#reviewCheckoutPlaceholder;

    if (
      !(reviewCheckout instanceof HTMLElement) ||
      !(mount instanceof HTMLElement) ||
      !placeholder
    )
      return;

    const isReviewStep = Boolean(mount);

    if (isReviewStep) {
      reviewCheckout.hidden = false;
      mount.appendChild(reviewCheckout);
      return;
    }

    if (reviewCheckout.closest(REVIEW_CHECKOUT_MOUNT_SELECTOR)) {
      placeholder.parentNode?.insertBefore(reviewCheckout, placeholder);
    }

    reviewCheckout.hidden = true;
  }

  /**
   * @param {{ resetStep?: boolean, replaceStepHistory?: boolean }} [options]
   */
  open(options = {}) {
    if (!this.#popup) return;

    this.#ensureReviewCheckoutReserved();
    this.#mountPopup();
    this.#preloadStepCardOptionImages(MIN_STEP);
    delete this.#popup.dataset.futCardPlayerGuidanceShown;
    this.#popup.hidden = false;
    this.#popup.setAttribute("aria-hidden", "false");
    this.#isOpen = true;

    if (!this.#isDesignMode) {
      document.body.classList.add(PERSONALIZATION_OPEN_CLASS);

      if (options.resetStep) {
        if (pushPersonalizationParam(this.#queryParam, this.#stepParam)) {
          this.#historyDepth = 1;
        }
      } else if (options.replaceStepHistory) {
        replacePersonalizationParam(this.#queryParam);
        this.#historyDepth = 0;
      } else if (!hasPersonalizationParam(this.#queryParam)) {
        if (pushPersonalizationParam(this.#queryParam, this.#stepParam)) {
          this.#historyDepth = 1;
        }
      }
    } else {
      this.#popup.dataset.editorOpen = "true";
    }

    let initialStep = options.resetStep
      ? MIN_STEP
      : getStepFromUrl(this.#stepParam, this.#totalSteps);

    if (!options.resetStep && !this.#isDesignMode) {
      const resolvedStep = this.#resolveInitialStep(initialStep);
      if (resolvedStep !== initialStep) {
        initialStep = resolvedStep;
        setStepParam(initialStep, this.#stepParam, true);
      }
    }

    this.#applyStep(initialStep, {
      updateHistory: !this.#isDesignMode && !options.resetStep,
      replaceHistory: options.replaceStepHistory ?? !options.resetStep,
    });

    this.#bindPopupAccessibility();
    this.#saveEditorState();

    const closeButton = this.#popup.querySelector(CLOSE_SELECTOR);
    if (closeButton instanceof HTMLElement && !this.#isDesignMode) {
      closeButton.focus();
    }
  }

  /**
   * @param {{ skipHistory?: boolean, skipEditorStateSave?: boolean }} [options]
   */
  close(options = {}) {
    if (!this.#popup) return;

    if (!this.#isDesignMode && !options.skipHistory && this.#historyDepth > 0) {
      const depth = this.#historyDepth;
      this.#historyDepth = 0;
      window.history.go(-depth);
      return;
    }

    if (!this.#isOpen) return;

    this.#unbindPopupAccessibility();

    this.#popup.hidden = true;
    this.#popup.setAttribute("aria-hidden", "true");
    document.body.classList.remove(PERSONALIZATION_OPEN_CLASS);
    this.#isOpen = false;

    if (this.#popupPlaceholder) {
      this.#popupPlaceholder.parentNode?.insertBefore(
        this.#popup,
        this.#popupPlaceholder,
      );
    }

    if (this.#isDesignMode) {
      this.#restoreReviewCheckout();
    }

    if (!this.#isDesignMode && !options.skipHistory) {
      clearPersonalizationParam(this.#queryParam, this.#stepParam);
    } else if (this.#isDesignMode) {
      delete this.#popup.dataset.editorOpen;
      delete this.#popup.dataset.editorStep;
      if (!options.skipEditorStateSave) {
        this.#saveEditorState();
      }
    }

    this.#historyDepth = 0;

    if (!this.#isDesignMode) {
      this.#trigger?.focus();
    }
  }

  /**
   * @param {number} step
   * @param {{ pushHistory?: boolean }} [options]
   */
  goToStep(step, options = {}) {
    const nextStep = Math.min(Math.max(step, MIN_STEP), this.#totalSteps);

    if (!this.#isDesignMode && nextStep > this.#currentStep) {
      const currentStepBlock = this.#stepBlocks[this.#currentStep - 1];
      if (
        currentStepBlock &&
        !validateFutCardPersonalizationStep(currentStepBlock)
      ) {
        return;
      }
    }

    this.#applyStep(nextStep, {
      updateHistory: !this.#isDesignMode,
      replaceHistory: !options.pushHistory,
    });
  }

  /**
   * @param {number} step
   * @param {{ updateHistory?: boolean, replaceHistory?: boolean }} [options]
   */
  #applyStep(step, options = {}) {
    if (!this.#popup) return;

    this.#currentStep = step;

    this.#popup.querySelectorAll(STEP_SELECTOR).forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;

      const panelStep = Number.parseInt(
        panel.getAttribute("data-fut-card-personalization-step") ?? "",
        10,
      );
      const isActive = panelStep === step;
      panel.hidden = !isActive;
      panel.toggleAttribute("data-active", isActive);
    });

    this.#popup
      .querySelectorAll(STEP_INDICATOR_SELECTOR)
      .forEach((indicator) => {
        const indicatorStep = Number.parseInt(
          indicator.getAttribute(
            "data-fut-card-personalization-step-indicator",
          ) ?? "",
          10,
        );
        let state = "upcoming";

        if (indicatorStep < step) state = "complete";
        if (indicatorStep === step) state = "current";

        indicator.setAttribute("data-state", state);
        indicator.toggleAttribute("aria-current", indicatorStep === step);
      });

    const backButton = this.#popup.querySelector(BACK_SELECTOR);
    if (backButton instanceof HTMLButtonElement) {
      const isFirstStep = step <= MIN_STEP;

      if (this.#hideUnavailableNavButtons) {
        backButton.hidden = isFirstStep;
        backButton.disabled = false;
      } else {
        backButton.hidden = false;
        backButton.disabled = isFirstStep;
      }
    }

    const nextButton = this.#popup.querySelector(NEXT_SELECTOR);
    if (nextButton instanceof HTMLButtonElement) {
      const isLastStep = step >= this.#totalSteps;

      if (this.#hideUnavailableNavButtons) {
        nextButton.hidden = isLastStep;
        nextButton.disabled = false;
      } else {
        nextButton.hidden = false;
        nextButton.disabled = isLastStep;
      }
    }

    const progressSummary = this.#popup.querySelector(
      PROGRESS_SUMMARY_SELECTOR,
    );
    if (progressSummary instanceof HTMLElement && this.#stepLabelTemplate) {
      progressSummary.textContent = this.#stepLabelTemplate
        .replace("%current%", String(step))
        .replace("%total%", String(this.#totalSteps));
    }

    const progressFill = this.#popup.querySelector(PROGRESS_FILL_SELECTOR);
    if (progressFill instanceof HTMLElement) {
      const fillPercent =
        this.#totalSteps <= 1
          ? 100
          : ((step - 1) / (this.#totalSteps - 1)) * 100;
      progressFill.style.width = `${fillPercent}%`;
    }

    if (options.updateHistory) {
      const didUpdate = setStepParam(
        step,
        this.#stepParam,
        options.replaceHistory !== false,
      );

      if (didUpdate && options.replaceHistory === false) {
        this.#historyDepth++;
      }
    }

    this.#syncReviewCheckoutMount(step);

    if (step === MIN_STEP) {
      this.#preloadStepCardOptionImages(MIN_STEP);
    }

    if (this.#isDesignMode && this.#popup) {
      this.#popup.dataset.editorStep = String(step);
      this.#saveEditorState();
    }

    if (this.#mobileMediaQuery?.matches) {
      this.#enableMobileSinglePage();
    }

    this.#popup.dispatchEvent(
      new CustomEvent("fut-card:personalization-step", {
        bubbles: true,
        detail: { step },
      }),
    );
  }

  /**
   * @param {number} requestedStep
   * @returns {number}
   */
  #resolveInitialStep(requestedStep) {
    if (requestedStep <= MIN_STEP) return MIN_STEP;

    for (let step = MIN_STEP; step < requestedStep; step++) {
      const stepBlock = this.#stepBlocks[step - 1];
      if (stepBlock && !validateFutCardPersonalizationStep(stepBlock)) {
        return step;
      }
    }

    return requestedStep;
  }

  #bindPopupAccessibility() {
    if (this.#popupAccessibilityBound) return;

    if (!this.#isDesignMode) {
      document.addEventListener("keydown", this.#handleDocumentKeyDown);
      this.#setPageInert(true);
    }

    this.#popupAccessibilityBound = true;
  }

  #unbindPopupAccessibility() {
    if (!this.#popupAccessibilityBound) return;

    if (!this.#isDesignMode) {
      document.removeEventListener("keydown", this.#handleDocumentKeyDown);
      this.#setPageInert(false);
    }

    this.#popupAccessibilityBound = false;
  }

  /**
   * @param {boolean} enabled
   */
  #setPageInert(enabled) {
    const popup = this.#popup;
    if (!popup) return;

    for (const child of document.body.children) {
      if (child === popup) continue;

      if (enabled) {
        child.setAttribute("inert", "");
      } else {
        child.removeAttribute("inert");
      }
    }
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getPopupFocusableElements() {
    const popup = this.#popup;
    if (!popup) return [];

    /** @type {HTMLElement[]} */
    const focusable = [];

    popup.querySelectorAll(FOCUSABLE_SELECTOR).forEach((element) => {
      if (!(element instanceof HTMLElement)) return;
      if (element.closest("[hidden]")) return;
      if (element.hasAttribute("disabled")) return;

      focusable.push(element);
    });

    return focusable;
  }

  /** @param {KeyboardEvent} event */
  #handleDocumentKeyDown = (event) => {
    if (!this.#isOpen || !this.#popup) return;

    if (event.key === "Escape") {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== "Tab") return;

    const focusable = this.#getPopupFocusableElements();
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;

    const active = document.activeElement;

    if (event.shiftKey) {
      if (active === first || !this.#popup.contains(active)) {
        event.preventDefault();
        last.focus();
      }

      return;
    }

    if (active === last || !this.#popup.contains(active)) {
      event.preventDefault();
      first.focus();
    }
  };

  /**
   * @param {string | undefined} blockId
   * @param {Element | null} [target]
   * @returns {boolean}
   */
  shouldOpenEditorPopupForBlockSelect(blockId, target = null) {
    this.#refreshReviewCheckoutReference();

    if (target && this.#isEditorSelectionInsidePopup(target)) return true;
    if (target && this.#isEditorSelectionInsideReviewCheckout(target))
      return true;
    if (blockId && this.#matchesReviewCheckoutEditorBlockId(blockId))
      return true;
    // @ts-expect-error Theme editor bridge for cross-module guidance preview.
    if (window.__isFutCardGuidanceEditorBlockSelection?.(blockId, target)) {
      return true;
    }
    if (blockId && blockId.includes("player-image-guidance")) return true;
    if (!blockId) return false;
    return this.#shouldOpenEditorPopupForBlockId(blockId);
  }

  /**
   * @param {Element} target
   * @returns {boolean}
   */
  #isEditorSelectionInsidePopup(target) {
    const popup = this.#getPopupElement();
    if (popup?.contains(target)) return true;

    const popupRoot = target.closest(POPUP_SELECTOR);
    if (popupRoot instanceof HTMLElement && this.#ownsPopupElement(popupRoot)) {
      return true;
    }

    if (!this.contains(target)) return false;

    return Boolean(
      target.closest(
        `${POPUP_SELECTOR}, ${STEP_BLOCK_SELECTOR}, [data-fut-card-personalization-steps]`,
      ),
    );
  }

  /**
   * @param {HTMLElement} popup
   * @returns {boolean}
   */
  #ownsPopupElement(popup) {
    return popup === this.#popup || this.contains(popup);
  }

  /**
   * @param {string} blockId
   * @returns {boolean}
   */
  #shouldOpenEditorPopupForBlockId(blockId) {
    const normalizedBlockId = this.#normalizeEditorBlockId(blockId);

    if (
      blockId === this.dataset.editorPopupBlockId ||
      normalizedBlockId === this.dataset.editorPopupBlockId ||
      blockId === this.#popup?.dataset.editorPopupBlockId ||
      normalizedBlockId === this.#popup?.dataset.editorPopupBlockId
    ) {
      return true;
    }

    this.#loadEditorStepMap();

    return this.#lookupEditorStep(blockId) != null;
  }

  /**
   * @param {string} blockId
   * @param {Element | null} [target]
   */
  handleEditorPopupBlockSelect(blockId, target = null) {
    if (!this.#isDesignMode && !window.Shopify?.designMode) return;

    this.#refreshReviewCheckoutReference();
    this.#ensureEditorPopupReference();
    this.#syncPopupFromDom();

    const reviewCheckoutStep = this.#findReviewCheckoutEditorStep();
    const isReviewCheckoutSelection =
      this.#matchesReviewCheckoutEditorBlockId(blockId) ||
      (target != null && this.#isEditorSelectionInsideReviewCheckout(target));

    const step =
      this.#getEditorStepForBlockId(blockId) ??
      (isReviewCheckoutSelection ? reviewCheckoutStep : null) ??
      (target ? this.#getEditorStepForTarget(target) : null);

    if (!this.#isOpen) {
      this.open({ replaceStepHistory: true });
    }

    if (step != null && step >= MIN_STEP) {
      this.goToStep(step);
    }

    if (this.#popup) {
      this.#popup.dataset.editorStep = String(this.#currentStep);
    }

    this.#openGuidanceEditorPreviewIfNeeded(blockId, target);
  }

  /**
   * @param {string} blockId
   * @param {Element | null} [target]
   */
  #openGuidanceEditorPreviewIfNeeded(blockId, target = null) {
    const isGuidanceSelection =
      // @ts-expect-error Theme editor bridge for cross-module guidance preview.
      window.__isFutCardGuidanceEditorBlockSelection?.(blockId, target) ===
      true;

    if (!isGuidanceSelection) return;

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        // @ts-expect-error Theme editor bridge for cross-module guidance preview.
        window.__openFutCardGuidanceEditor?.(blockId, target);
      });
    });
  }

  closeEditorPopupIfOpen() {
    if (!this.#isOpen) return;
    this.close({ skipHistory: true });
  }

  /** @param {Event} event */
  handleEditorSectionLoad(event) {
    const section = event.target;
    if (!(section instanceof HTMLElement)) return;
    if (!section.contains(this)) return;

    const savedState = readFutCardEditorSessionState();
    const shouldRestore = this.#isOpen || savedState?.isOpen === true;
    const stepToRestore = Number.parseInt(
      String(savedState?.step ?? this.#currentStep),
      10,
    );

    this.#trigger = this.querySelector(TRIGGER_SELECTOR);
    this.#refreshReviewCheckoutReference();
    this.#resyncPopupReference();

    if (!shouldRestore) return;

    this.#isOpen = false;
    this.open({ replaceStepHistory: true });

    if (!Number.isNaN(stepToRestore) && stepToRestore >= MIN_STEP) {
      this.goToStep(stepToRestore);
    }
  }

  #saveEditorState() {
    if (!this.#isDesignMode && !window.Shopify?.designMode) return;

    sessionStorage.setItem(
      EDITOR_STATE_KEY,
      JSON.stringify({
        isOpen: this.#isOpen,
        step: this.#currentStep,
      }),
    );
  }

  restoreEditorPopupFromSavedState() {
    this.#restoreEditorState();
  }

  #restoreEditorState() {
    if (!this.#isDesignMode && !window.Shopify?.designMode) return;

    const state = readFutCardEditorSessionState();
    if (!state?.isOpen || this.#isOpen) return;

    this.#refreshReviewCheckoutReference();
    this.#ensureEditorPopupReference();
    if (!this.#popup) return;

    const step = Number.parseInt(String(state.step ?? ""), 10);
    const resolvedStep =
      !Number.isNaN(step) && step >= MIN_STEP ? step : MIN_STEP;

    this.open({ replaceStepHistory: true });
    this.goToStep(resolvedStep);
  }

  #ensureEditorPopupReference() {
    if (this.#popup instanceof HTMLElement) return;

    const inlinePopup = this.querySelector(POPUP_SELECTOR);
    if (!(inlinePopup instanceof HTMLElement)) return;

    this.#popup = inlinePopup;
    this.#stepParam = this.#popup.dataset.stepParam || DEFAULT_STEP_PARAM;
    this.#stepLabelTemplate = this.#popup.dataset.stepLabelTemplate ?? "";
    this.#hideUnavailableNavButtons =
      this.#popup.dataset.navButtonAvailability === "hidden";
    this.#initializeSteps();
    this.#bindPopupControls();
  }

  #loadEditorStepMap() {
    const popup = this.#getPopupElement();
    if (!(popup instanceof HTMLElement)) {
      this.#editorStepMap = new Map();
      this.#reviewCheckoutEditorBlockIds = new Set();
      return;
    }

    /** @type {Map<string, number>} */
    const map = new Map();
    this.#reviewCheckoutEditorBlockIds = new Set();
    const ids = popup.dataset.editorBlockIds?.split("|").filter(Boolean) ?? [];
    const steps = popup.dataset.editorBlockSteps?.split("|") ?? [];

    ids.forEach((id, index) => {
      const step = Number.parseInt(steps[index] ?? "", 10);
      if (!Number.isNaN(step)) {
        this.#registerEditorStepBlockId(map, id, step);
      }
    });

    if (this.#isDesignMode || window.Shopify?.designMode) {
      this.#buildEditorStepMapFromDom(popup, map);
      this.#registerReviewCheckoutEditorBlocks(map);
    }

    this.#editorStepMap = map;
  }

  #refreshReviewCheckoutReference() {
    if (!this.#isDesignMode && !window.Shopify?.designMode) return;

    if (this.#reviewCheckout?.isConnected) return;

    const placeholder = this.#reviewCheckoutPlaceholder;
    if (placeholder?.parentElement) {
      const sibling = placeholder.nextElementSibling;
      if (
        sibling instanceof HTMLElement &&
        sibling.matches(REVIEW_CHECKOUT_SELECTOR)
      ) {
        this.#reviewCheckout = sibling;
        return;
      }

      const checkoutInParent = placeholder.parentElement.querySelector(
        REVIEW_CHECKOUT_SELECTOR,
      );
      if (checkoutInParent instanceof HTMLElement) {
        this.#reviewCheckout = checkoutInParent;
        return;
      }
    }

    const liveCheckout = document.querySelector(REVIEW_CHECKOUT_SELECTOR);
    if (liveCheckout instanceof HTMLElement) {
      this.#reviewCheckout = liveCheckout;
    }
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getReviewCheckoutElement() {
    this.#refreshReviewCheckoutReference();

    if (this.#reviewCheckout instanceof HTMLElement) {
      return this.#reviewCheckout;
    }

    const reviewCheckout = document.querySelector(REVIEW_CHECKOUT_SELECTOR);
    return reviewCheckout instanceof HTMLElement ? reviewCheckout : null;
  }

  /**
   * @param {string} blockId
   * @returns {boolean}
   */
  #matchesReviewCheckoutEditorBlockId(blockId) {
    if (!blockId || this.#findReviewCheckoutEditorStep() == null) return false;

    const normalizedBlockId = this.#normalizeEditorBlockId(blockId);
    const staticBlockId = this.dataset.editorReviewCheckoutBlockId;

    if (staticBlockId) {
      if (
        blockId === staticBlockId ||
        normalizedBlockId === staticBlockId ||
        blockId.endsWith(`__${staticBlockId}`)
      ) {
        return true;
      }
    }

    const childBlockIds =
      this.dataset.editorReviewCheckoutChildBlockIds
        ?.split("|")
        .filter(Boolean) ?? [];

    for (const childBlockId of childBlockIds) {
      if (
        blockId === childBlockId ||
        normalizedBlockId === childBlockId ||
        blockId.endsWith(`__${childBlockId}`)
      ) {
        return true;
      }
    }

    this.#loadEditorStepMap();

    return (
      this.#reviewCheckoutEditorBlockIds.has(blockId) ||
      this.#reviewCheckoutEditorBlockIds.has(normalizedBlockId)
    );
  }

  /**
   * @returns {number | null}
   */
  #findReviewCheckoutEditorStep() {
    const popup = this.#getPopupElement();
    if (!popup) return null;

    const stepBlocks = popup.querySelectorAll(STEP_BLOCK_SELECTOR);

    for (let index = 0; index < stepBlocks.length; index++) {
      const stepBlock = stepBlocks[index];
      if (!(stepBlock instanceof HTMLElement)) continue;
      if (stepBlock.querySelector(REVIEW_CHECKOUT_MOUNT_SELECTOR)) {
        return index + 1;
      }
    }

    return null;
  }

  /**
   * @param {Map<string, number>} map
   */
  /**
   * @param {Map<string, number>} map
   * @param {string} blockId
   * @param {number} step
   */
  #registerReviewCheckoutEditorBlockId(map, blockId, step) {
    this.#registerEditorStepBlockId(map, blockId, step);
    this.#reviewCheckoutEditorBlockIds.add(blockId);
    this.#reviewCheckoutEditorBlockIds.add(
      this.#normalizeEditorBlockId(blockId),
    );
  }

  /**
   * @param {Map<string, number>} map
   */
  #registerReviewCheckoutEditorBlocks(map) {
    const step = this.#findReviewCheckoutEditorStep();
    if (!step) return;

    const staticBlockId = this.dataset.editorReviewCheckoutBlockId;
    if (staticBlockId) {
      this.#registerReviewCheckoutEditorBlockId(map, staticBlockId, step);
    }

    const childBlockIds =
      this.dataset.editorReviewCheckoutChildBlockIds
        ?.split("|")
        .filter(Boolean) ?? [];

    for (const childBlockId of childBlockIds) {
      this.#registerReviewCheckoutEditorBlockId(map, childBlockId, step);
    }

    const reviewCheckout = this.#getReviewCheckoutElement();
    if (!(reviewCheckout instanceof HTMLElement)) return;

    const rootBlockId = this.#readEditorBlockIdFromElement(reviewCheckout);
    if (rootBlockId) {
      this.#registerReviewCheckoutEditorBlockId(map, rootBlockId, step);
    }

    reviewCheckout
      .querySelectorAll("[data-shopify-editor-block], [id^='shopify-block-']")
      .forEach((element) => {
        if (!(element instanceof HTMLElement)) return;

        const blockId = this.#readEditorBlockIdFromElement(element);
        if (blockId) {
          this.#registerReviewCheckoutEditorBlockId(map, blockId, step);
        }
      });
  }

  /**
   * @param {Element} target
   * @returns {boolean}
   */
  #isEditorSelectionInsideReviewCheckout(target) {
    if (target.closest("[data-fut-card-review-checkout-editor-anchor]")) {
      return true;
    }

    const reviewCheckout = this.#getReviewCheckoutElement();
    if (reviewCheckout?.contains(target)) return true;

    const reviewCheckoutRoot = target.closest(REVIEW_CHECKOUT_SELECTOR);
    return reviewCheckoutRoot instanceof HTMLElement;
  }

  /**
   * @param {Map<string, number>} map
   * @param {string} blockId
   * @param {number} step
   */
  #registerEditorStepBlockId(map, blockId, step) {
    map.set(blockId, step);
    map.set(this.#normalizeEditorBlockId(blockId), step);
  }

  /**
   * @param {HTMLElement} popup
   * @param {Map<string, number>} map
   */
  #buildEditorStepMapFromDom(popup, map) {
    popup.querySelectorAll(STEP_BLOCK_SELECTOR).forEach((stepBlock, index) => {
      if (!(stepBlock instanceof HTMLElement)) return;

      const step = index + 1;
      const blockId = this.#readEditorBlockIdFromElement(stepBlock);
      if (blockId) this.#registerEditorStepBlockId(map, blockId, step);

      stepBlock
        .querySelectorAll("[data-shopify-editor-block], [id^='shopify-block-']")
        .forEach((element) => {
          if (!(element instanceof HTMLElement)) return;

          const nestedBlockId = this.#readEditorBlockIdFromElement(element);
          if (nestedBlockId) {
            this.#registerEditorStepBlockId(map, nestedBlockId, step);
          }
        });
    });
  }

  /**
   * @param {Element} element
   * @returns {string | null}
   */
  #readEditorBlockIdFromElement(element) {
    const editorBlockAttr = element.getAttribute("data-shopify-editor-block");
    if (editorBlockAttr) {
      try {
        const data = JSON.parse(editorBlockAttr);
        if (data?.id) return String(data.id);
      } catch {
        /* ignore malformed editor metadata */
      }
    }

    if (element.id.startsWith("shopify-block-")) {
      return element.id.slice("shopify-block-".length);
    }

    return null;
  }

  /**
   * @param {string} blockId
   * @returns {string}
   */
  #normalizeEditorBlockId(blockId) {
    const separatorIndex = blockId.lastIndexOf("__");
    if (separatorIndex === -1) return blockId;
    return blockId.slice(separatorIndex + 1);
  }

  /**
   * @param {string} blockId
   * @returns {number | null}
   */
  #lookupEditorStep(blockId) {
    const normalizedBlockId = this.#normalizeEditorBlockId(blockId);

    if (this.#editorStepMap.has(blockId)) {
      return this.#editorStepMap.get(blockId) ?? null;
    }

    if (this.#editorStepMap.has(normalizedBlockId)) {
      return this.#editorStepMap.get(normalizedBlockId) ?? null;
    }

    for (const [mappedBlockId, step] of this.#editorStepMap.entries()) {
      const normalizedMappedBlockId =
        this.#normalizeEditorBlockId(mappedBlockId);

      if (
        mappedBlockId === blockId ||
        mappedBlockId === normalizedBlockId ||
        normalizedMappedBlockId === blockId ||
        normalizedMappedBlockId === normalizedBlockId ||
        blockId.endsWith(`__${normalizedMappedBlockId}`) ||
        mappedBlockId.endsWith(`__${normalizedBlockId}`)
      ) {
        return step;
      }
    }

    return null;
  }

  /**
   * @param {Element} target
   * @returns {number | null}
   */
  #getEditorStepForTarget(target) {
    const stepBlock = target.closest(STEP_BLOCK_SELECTOR);
    if (!(stepBlock instanceof HTMLElement)) return null;

    const stepBlocks =
      this.#getPopupElement()?.querySelectorAll(STEP_BLOCK_SELECTOR);
    if (!stepBlocks) return null;

    const stepIndex = Array.from(stepBlocks).indexOf(stepBlock);
    if (stepIndex === -1) return null;

    return stepIndex + 1;
  }

  /**
   * @param {string} blockId
   * @returns {number | null}
   */
  #getEditorStepForBlockId(blockId) {
    this.#loadEditorStepMap();
    return this.#lookupEditorStep(blockId);
  }

  #getPopupElement() {
    if (this.#popup instanceof HTMLElement) return this.#popup;
    const inlinePopup = this.querySelector(POPUP_SELECTOR);
    return inlinePopup instanceof HTMLElement ? inlinePopup : null;
  }

  #syncPopupFromDom() {
    const inlinePopup = this.#getPopupElement();
    if (!(inlinePopup instanceof HTMLElement)) return;
    if (inlinePopup === this.#popup) {
      this.#loadEditorStepMap();
      return;
    }

    const wasOpen = this.#isOpen;
    const currentStep = this.#currentStep;

    this.#unbindPopupControls();
    this.#unbindPopupAccessibility();

    if (
      this.#popup instanceof HTMLElement &&
      this.#popup !== inlinePopup &&
      this.#popup.parentElement === document.body
    ) {
      this.#popup.remove();
    }

    this.#popup = inlinePopup;
    this.#stepParam = this.#popup.dataset.stepParam || DEFAULT_STEP_PARAM;
    this.#stepLabelTemplate = this.#popup.dataset.stepLabelTemplate ?? "";
    this.#hideUnavailableNavButtons =
      this.#popup.dataset.navButtonAvailability === "hidden";

    this.#initializeSteps();
    this.#bindPopupControls();
    this.#loadEditorStepMap();

    if (wasOpen) {
      this.#isOpen = false;
      this.open({ replaceStepHistory: true });
      this.goToStep(currentStep);
    }
  }

  #resyncPopupReference() {
    const freshPopup =
      this.querySelector(POPUP_SELECTOR) ?? this.#getPopupElement();
    if (!(freshPopup instanceof HTMLElement)) return;

    const wasOpen = this.#isOpen;
    const currentStep = this.#currentStep;
    const stalePopup = this.#popup;

    if (freshPopup === stalePopup) {
      this.#initializeSteps();
      this.#loadEditorStepMap();
      return;
    }

    this.#unbindPopupControls();
    this.#unbindPopupAccessibility();

    if (
      stalePopup instanceof HTMLElement &&
      stalePopup !== freshPopup &&
      stalePopup.parentElement === document.body
    ) {
      stalePopup.remove();
    }

    this.#popup = freshPopup;
    this.#stepParam = this.#popup.dataset.stepParam || DEFAULT_STEP_PARAM;
    this.#stepLabelTemplate = this.#popup.dataset.stepLabelTemplate ?? "";
    this.#hideUnavailableNavButtons =
      this.#popup.dataset.navButtonAvailability === "hidden";

    if (!this.#popupPlaceholder?.parentNode) {
      this.#popupPlaceholder = document.createComment(
        "fut-card-personalization-popup",
      );
    }

    if (
      this.#popup.parentElement !== document.body &&
      (!wasOpen || !this.#isDesignMode)
    ) {
      this.#popup.replaceWith(this.#popupPlaceholder);
    }

    this.#initializeSteps();
    this.#bindPopupControls();
    this.#loadEditorStepMap();

    if (wasOpen) {
      this.#isOpen = false;
      this.open({ replaceStepHistory: true });
      this.goToStep(currentStep);
    }
  }
}

if (!customElements.get("fut-card-personalization")) {
  customElements.define("fut-card-personalization", FutCardPersonalization);
}

ensureFutCardGlobalThemeEditorEvents(FutCardPersonalization);
